import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { SwiperModule } from 'swiper/angular';

import { CmsRenderCoreModule } from './cms-render.core.module';
import { CmsContentRenderComponent } from './ui/cms-content-render/cms-content-render.component';
import { CmsAkamaiDynamicPosterVideoDirective } from './ui/directives/cms-akamai-dynamic-poster-video.directive';
import {
    CmsAkamaiWidthImageDirective,
} from './ui/directives/cms-akamai-width-image.directive';
import { CmsPlayOnViewDirective } from './ui/directives/cms-play-on-view.directive';
import { DeferLoadDirective } from './ui/directives/defer-load.directive';
import { LinkTransformer } from './ui/pipes/link-transformer/link-transformer.pipe';
import { IdtTrustHtmlPipe } from './ui/pipes/trust-html/trust-html.pipe';
import { RenderLinkComponent } from './ui/render-blocks/render-link/render-link.component';
import {
    SliceFlexCloseComponent,
} from './ui/render-blocks/slice-flex/components/slice-flex-close/slice-flex-close.component';
import { SliceFlexComponent } from './ui/render-blocks/slice-flex/slice-flex.component';
import { SliceStackedComponent } from './ui/render-blocks/slice-stacked/slice-stacked.component';
import { SliceTabsComponent } from './ui/render-blocks/slice-tabs/slice-tabs.component';
import { SlotManagerComponent } from './ui/render-blocks/slot-manager/slot-manager.component';
import { SwitchContentComponent } from './ui/render-blocks/switch-content/switch-content.component';
import {
    LayoutFragmentsComponent,
} from './ui/render-layouts/layout-fragments/layout-fragments.component';
import {
    LayoutFullWidthComponent,
} from './ui/render-layouts/layout-full-width/layout-full-width.component';
import {
    LayoutHeaderFooterComponent,
} from './ui/render-layouts/layout-header-footer/layout-header-footer.component';
import {
    LayoutVerticalSliderComponent,
} from './ui/render-layouts/layout-vertical-slider/layout-vertical-slider.component';
import { SliderLogoSectionsComponent } from './ui/render-layouts/slider-logo-sections/slider-logo-sections.component';
import { MdSkeletonComponent } from './ui/render-pieces/md-skeleton/md-skeleton.component';
import { PieceButtonComponent } from './ui/render-pieces/piece-button/piece-button.component';
import {
    PieceCollapsibleComponent,
} from './ui/render-pieces/piece-collapsible/piece-collapsible.component';
import { PieceCountdownComponent } from './ui/render-pieces/piece-countdown/piece-countdown.component';
import {
    FixImageHeightDirective,
} from './ui/render-pieces/piece-image/fix-image-height.directive';
import { LazyPieceImageDirective } from './ui/render-pieces/piece-image/lazy-piece-image.directive';
import { PieceImageComponent } from './ui/render-pieces/piece-image/piece-image.component';
import { PieceProductCarouselModule } from './ui/render-pieces/piece-product-carousel/piece-product-carousel.module';
import {
    PieceSelectStoreLanguageComponent,
} from './ui/render-pieces/piece-select-store-language/piece-select-store-language.component';
import {
    PieceSeoCategoriesComponent,
} from './ui/render-pieces/piece-seo-categories/piece-seo-categories.component';
import { PieceSeparatorComponent } from './ui/render-pieces/piece-separator/piece-separator.component';
import { PieceTextComponent } from './ui/render-pieces/piece-text/piece-text.component';
import { PieceVideoComponent } from './ui/render-pieces/piece-video/piece-video.component';
import { RenderWysiwygComponent } from './ui/render-pieces/render-wysiwyg/render-wysiwyg.component';
import { SliceSliderComponent } from './ui/render-pieces/slice-slider/slice-slider.component';
import { AssetsSharedService } from './ui/services/assets-shared/assets-shared.service';
import { GenerateSrcsetService } from './ui/services/assets-shared/generate-srcset.service';
import { LinksService } from './ui/services/links/links.service';
import { AKAMAI_IMPOLICY } from './ui/tokens/cms.render-ui.tokens';

import { ElementObserverDirective } from '@shared/directives/element-observer.directive';
import { LazyLoadModule } from '@shared/lazy-load/lazy-load.module';
import { MarketModule } from '@shared/market/market.module';
import { UiSvgIconModule } from '@shared/ui/svg-icon/svg-icon.module';

const layouts = [
    LayoutFragmentsComponent,
    LayoutFullWidthComponent,
    LayoutHeaderFooterComponent,
    LayoutVerticalSliderComponent,
];
const blocks = [
    RenderLinkComponent,
    SliceFlexComponent,
    SliceStackedComponent,
    SlotManagerComponent,
    SwitchContentComponent,
    SliceFlexCloseComponent,
    SliceTabsComponent,
];
const pieces = [
    MdSkeletonComponent,
    PieceButtonComponent,
    PieceCollapsibleComponent,
    PieceCountdownComponent,
    PieceImageComponent,
    PieceSelectStoreLanguageComponent,
    PieceSeoCategoriesComponent,
    PieceSeparatorComponent,
    PieceTextComponent,
    PieceVideoComponent,
    RenderWysiwygComponent,
    SliceSliderComponent,
    SliceFlexCloseComponent,

];
const pipes = [
    LinkTransformer,
];

@NgModule({
    declarations: [
        CmsAkamaiDynamicPosterVideoDirective,
        ...layouts,
        ...pieces,
        ...blocks,
        ...pipes,
        CmsAkamaiWidthImageDirective,
        CmsContentRenderComponent,
        CmsPlayOnViewDirective,
        DeferLoadDirective,
        FixImageHeightDirective,
        IdtTrustHtmlPipe,
        LazyPieceImageDirective,
    ],
    providers: [
        {
            provide: AKAMAI_IMPOLICY,
            useFactory: (store) => {
                return store.state().core.context.store.cmsConfigurations.reduce((acc, curr) => {
                    if (curr.key === 'akamai_impolicy') {
                        return curr.webApp;
                    }
                    return acc;
                }, null);
            },
            deps: [
                Store,
            ],
        },
        AssetsSharedService,
        GenerateSrcsetService,
        LinksService,
    ],
    imports: [
        CmsRenderCoreModule,
        CommonModule,
        TranslateModule.forChild(),
        ElementObserverDirective,
        LazyLoadModule,
        MarketModule,
        NgOptimizedImage,
        PieceProductCarouselModule,
        SliderLogoSectionsComponent,
        SwiperModule,
        UiSvgIconModule,
    ],
    exports: [
        CmsContentRenderComponent,
        ...layouts,
        ...pieces,
        ...blocks,
        ...pipes,
    ],
})
export class CmsRenderUiModule {
    public get cmsContentRenderComponent() {
        return CmsContentRenderComponent;
    }
}
